import React from 'react';
import { MailOutlined } from '@ant-design/icons';
import { capitalCase } from 'change-case';
import dayjs from 'dayjs';
import Fuse from 'fuse.js';
import { useIntl } from 'react-intl';
import { Resizable } from 'react-resizable';
import type { ResizableProps } from 'react-resizable';
import {
  Layout,
  Table,
  Typography,
  message,
  Button,
  Popconfirm,
  Spin,
  Tooltip,
} from 'antd';
import type { ColumnType } from 'antd/es/table';
import {
  EMPTY,
  PRETTY_DATE_FORMAT,
  useEffectWithPrev,
} from '@owl-frontend/components';
import { type UserAdmin, UserRole } from '../../../api/interface';
import { Locale, SessionContext } from '../../../context/AppProvider';
import { FeatureKeys } from '../../../data/session/interface';
import useUsersHook from './useUsersHook';
import DeleteUsersComponent from './DeleteUsersComponent';
import NewUserModal, { type UserForm } from './NewUserModal';
import styles from './UsersContainer.module.scss';
import UsersFilterComponent from './UsersFilterComponent';

interface UserData {
  key: string;
  user: string;
  email: string;
  roles: string;
  date_created: string;
  last_active: string;
  status?: string;
  client_user_id?: string;
}

const ResizableTitle: React.FC<ResizableProps> = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className={styles.resizeHandle}
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const UsersContainer: React.FC = () => {
  const { messages } = React.useContext(Locale);
  const { current, features } = React.useContext(SessionContext);
  const intl = useIntl();
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [filteredRole, setFilteredRole] = React.useState<string>('');
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);
  const [resentEmailTimestamps, setResentEmailTimestamps] = React.useState<
    Record<string, number>
  >(() => {
    const saved = localStorage.getItem('resentEmailTimestamps');
    return saved ? JSON.parse(saved) : {};
  });
  const [currentResendEmail, setCurrentResendEmail] = React.useState<
    string | null
  >(null);
  const [
    {
      usersAdmin = [],
      actions: {
        createUser,
        deleteUsers,
        resendSignInEmail,
        usersAdmin: fetchUsersAdmin,
      },
    },
    handler,
  ] = useUsersHook();

  const [columnWidths, setColumnWidths] = React.useState<{
    [key: string]: number;
  }>(() => {
    const savedWidths = localStorage.getItem('userColumnWidths');
    return savedWidths ? JSON.parse(savedWidths) : {};
  });

  const handleResize = React.useCallback(
    (key: string, minWidth: number, maxWidth: number) =>
      (
        _: React.MouseEvent<HTMLElement>,
        { size }: { size: { width: number; height: number } }
      ) => {
        const newWidth = Math.min(Math.max(size.width, minWidth), maxWidth);
        const newColumnWidths = { ...columnWidths, [key]: newWidth };
        setColumnWidths(newColumnWidths);
        localStorage.setItem(
          'userColumnWidths',
          JSON.stringify(newColumnWidths)
        );
      },
    [columnWidths]
  );

  const getColumnProps = React.useCallback(
    (props: ColumnType<UserData>): ColumnType<UserData> => ({
      ...props,
      onHeaderCell: () => ({
        width: props.width,
        onResize: handleResize(
          props.key as string,
          180,
          600
        ) as React.ReactEventHandler<any>,
      }),
    }),
    [handleResize]
  );

  const fuse = React.useMemo(() => {
    return new Fuse(usersAdmin, {
      isCaseSensitive: false,
      useExtendedSearch: true,
      keys: ['email', 'first_name', 'last_name', 'full_name'],
    });
  }, [usersAdmin]);

  const existingEmails = React.useMemo(
    () => usersAdmin.map((u) => u.email),
    [usersAdmin]
  );

  const filteredUsers = React.useMemo(() => {
    let usersToFilter = usersAdmin;
    if (searchValue && searchValue.trim() !== '') {
      usersToFilter = fuse
        .search(`'${searchValue.trim()}`)
        .map((result) => result.item as UserAdmin);
    }
    if (filteredRole) {
      usersToFilter = usersToFilter.filter((user) =>
        user.roles.some(
          (role) => role.toLowerCase() === filteredRole.toLowerCase()
        )
      );
    }

    return usersToFilter;
  }, [usersAdmin, searchValue, filteredRole, fuse]);

  const dataSource: UserData[] = React.useMemo(
    () =>
      filteredUsers?.map((user) => ({
        key: user.email,
        user: user.full_name,
        email: user.email,
        roles: user.roles?.map((role) => capitalCase(role)).join(', '),
        date_created: user.created_at,
        last_active: user.last_active,
        status: user.status || '-',
        client_user_id: user.client_user_id || '-',
      })),
    [filteredUsers]
  );

  const canShowResendButton = React.useCallback(
    (email: string): boolean => {
      const lastResentTime = resentEmailTimestamps[email];
      if (!lastResentTime) {
        return true;
      }

      const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
      return lastResentTime < twentyFourHoursAgo;
    },
    [resentEmailTimestamps]
  );

  const onResend = React.useCallback(
    async (email: string): Promise<void> => {
      setCurrentResendEmail(email);
      await messageApi.loading(
        intl.formatMessage(
          { id: 'users.resend.messages.loading' },
          { user: email }
        )
      );
      handler.resendSignInEmail(email);
    },
    [handler, intl, messageApi]
  );

  const columns: ColumnType<UserData>[] = React.useMemo(
    () => [
      getColumnProps({
        title: messages['users.list.headers.user'],
        dataIndex: 'user',
        key: 'user',
        sorter: (a, b) => a.user.localeCompare(b.user),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: columnWidths['user'] || 200,
        ellipsis: true,
      }),
      getColumnProps({
        title: messages['users.list.headers.email'],
        dataIndex: 'email',
        key: 'email',
        sorter: (a, b) => (a.key || '').localeCompare(b.key || ''),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: columnWidths['email'] || 200,
        ellipsis: true,
      }),
      getColumnProps({
        title: messages['users.list.headers.employee_id'],
        dataIndex: 'client_user_id',
        key: 'client_user_id',
        sorter: (a, b) =>
          (a.client_user_id || '').localeCompare(b.client_user_id || ''),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: columnWidths['client_user_id'] || 150,
        ellipsis: true,
      }),
      getColumnProps({
        title: messages['users.list.headers.roles'],
        dataIndex: 'roles',
        key: 'roles',
        sorter: (a, b) => a.roles[0].localeCompare(b.roles[0]),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: columnWidths['roles'] || 200,
        ellipsis: true,
      }),
      getColumnProps({
        title: messages['users.list.headers.account_status'],
        dataIndex: 'status',
        key: 'status',
        render: (value: string, record: UserData) => {
          const statusDisplay = messages[`users.status.${value}`] ?? '-';
          //These are the cognito statuses that need to be resendable, (not storable values):
          const needsResend =
            features[FeatureKeys.SSOEnabled] ||
            ['FORCE_CHANGE_PASSWORD', 'UNCONFIRMED', 'RESET_REQUIRED'].includes(
              value
            );

          if (needsResend) {
            const showResendButton = canShowResendButton(record.email);
            if (showResendButton) {
              if (hoveredRow !== record.email) {
                return statusDisplay;
              }
              return (
                <Popconfirm
                  title={
                    <>
                      {messages['resend_email.message']}
                      <br />
                      {messages['resend_email.detail']}
                    </>
                  }
                  onConfirm={() => onResend(record.email)}
                  okText="Resend"
                  cancelText="Cancel"
                  icon={null}
                >
                  <Button
                    type="primary"
                    size="small"
                    style={{
                      padding: '0 8px',
                      height: '22px',
                      lineHeight: '22px',
                    }}
                  >
                    {messages['users.resend.button']}
                    {<MailOutlined />}
                  </Button>
                </Popconfirm>
              );
            }
            return (
              <Tooltip title={messages['users.resend.cooldown']}>
                {statusDisplay}
              </Tooltip>
            );
          }
          return statusDisplay;
        },
        sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
        sortDirections: ['ascend', 'descend', 'ascend'],
        width: columnWidths['status'] || 210,
        ellipsis: true,
      }),
      getColumnProps({
        title: messages['users.list.headers.last_active'],
        dataIndex: 'last_active',
        key: 'last_active',
        render: (value: string) => {
          return value ? dayjs(value).format(PRETTY_DATE_FORMAT) : EMPTY;
        },
        sorter: (a, b) => {
          const aValue = a.last_active
            ? dayjs(a.last_active).valueOf()
            : -Infinity;
          const bValue = b.last_active
            ? dayjs(b.last_active).valueOf()
            : -Infinity;
          return aValue - bValue;
        },
        sortDirections: ['descend', 'ascend', 'descend'],
        width: columnWidths['last_active'] || 200,
        ellipsis: true,
      }),
      getColumnProps({
        title: messages['users.list.headers.date_created'],
        dataIndex: 'date_created',
        key: 'date_created',
        render: (value: string) => {
          return dayjs(value).format(PRETTY_DATE_FORMAT);
        },
        sorter: (a, b) =>
          dayjs(a.date_created).valueOf() - dayjs(b.date_created).valueOf(),
        sortDirections: ['descend', 'ascend', 'descend'],
        width: columnWidths['date_created'] || 200,
        ellipsis: true,
      }),
    ],
    [
      getColumnProps,
      messages,
      columnWidths,
      features,
      canShowResendButton,
      hoveredRow,
      onResend,
    ]
  );

  const onCreate = React.useCallback(
    async (user: UserForm): Promise<void> => {
      await messageApi.loading(
        intl.formatMessage(
          { id: 'users.create.messages.loading' },
          { user: user.email }
        )
      );
      const newUser = {
        ...user,
        roles: Array.isArray(user.roles) ? user.roles : [user.roles], // Ensure roles are an array
      };
      handler.createUser(newUser);
    },
    [handler, intl, messageApi]
  );

  const onDelete = React.useCallback(async (): Promise<void> => {
    await messageApi.loading(
      intl.formatMessage(
        { id: 'users.delete.messages.loading' },
        { num_users: selectedRowKeys.length }
      )
    );
    handler.deleteUsers(selectedRowKeys as string[]);
  }, [handler, intl, messageApi, selectedRowKeys]);

  const onSelectChange = React.useCallback(
    (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    []
  );

  const rowSelection = React.useMemo(() => {
    return {
      selectedRowKeys,
      onChange: onSelectChange,
      getCheckboxProps: (record: { key: string }) => ({
        disabled: record.key === current?.email,
      }),
    };
  }, [selectedRowKeys, onSelectChange, current]);

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }
      messageApi.destroy(); // remove the pending message
      if (createUser.status === 'fulfilled') {
        messageApi.success(
          intl.formatMessage(
            { id: 'users.create.messages.success' },
            { user: createUser.result.email, role: createUser.result.roles[0] }
          )
        );
        setIsModalVisible(false);
        handler.fetchUsersAdmin();
      } else {
        messageApi.error(messages['users.create.messages.failure']);
      }
    },
    [createUser.status, createUser, messageApi, handler, messages, intl]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }
      messageApi.destroy(); // remove the pending message
      if (deleteUsers.status === 'fulfilled') {
        messageApi.success(
          selectedRowKeys.length === 1
            ? intl.formatMessage(
                { id: 'users.delete.messages.success' },
                { user: deleteUsers.result[0] }
              )
            : intl.formatMessage(
                { id: 'users.delete.messages.successes' },
                { num_users: selectedRowKeys.length }
              )
        );
        setSelectedRowKeys([]);
        handler.fetchUsersAdmin();
      } else {
        messageApi.error(messages['users.delete.messages.failure']);
      }
    },
    [
      deleteUsers.status,
      deleteUsers,
      messageApi,
      handler,
      messages,
      intl,
      selectedRowKeys.length,
    ]
  );
  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }
      messageApi.destroy();
      if (resendSignInEmail.status === 'fulfilled') {
        messageApi.success(messages['users.resend.messages.success']);
        if (currentResendEmail) {
          const timestamp = Date.now();
          const newTimestamps = {
            ...resentEmailTimestamps,
            [currentResendEmail]: timestamp,
          };
          setResentEmailTimestamps(newTimestamps);
          localStorage.setItem(
            'resentEmailTimestamps',
            JSON.stringify(newTimestamps)
          );
          setCurrentResendEmail(null);
        }
      } else {
        messageApi.error(messages['users.resend.messages.failure']);
        setCurrentResendEmail(null);
      }
    },
    [
      resendSignInEmail.status,
      resendSignInEmail,
      messageApi,
      messages,
      intl,
      resentEmailTimestamps,
      currentResendEmail,
    ]
  );

  React.useEffect(() => {
    handler.fetchUsersAdmin();
  }, [handler]);

  if (
    !current ||
    (!current.roles.includes(UserRole.Manager) &&
      !current.roles.includes(UserRole.Manager.toLowerCase()))
  ) {
    return null;
  }

  return (
    <Layout className={styles.container}>
      <Typography.Title className={styles.title} level={4}>
        {messages['users.title']}
      </Typography.Title>
      {selectedRowKeys.length === 0 ? (
        <UsersFilterComponent
          setFilteredRole={setFilteredRole}
          setIsModalVisible={setIsModalVisible}
          setSearchValue={setSearchValue}
        />
      ) : (
        <DeleteUsersComponent
          deleteUsers={onDelete}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}
      <Spin
        spinning={
          usersAdmin.length === 0 && fetchUsersAdmin.status === 'pending'
        }
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          className={styles.table}
          rowSelection={rowSelection}
          tableLayout="fixed"
          scroll={{
            x: 'fixed',
            y: 'calc(100vh - 400px)',
          }}
          pagination={{
            position: ['bottomRight'],
            showSizeChanger: true,
            defaultPageSize: 10,
          }}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          onRow={(record) => ({
            onMouseEnter: () => setHoveredRow(record.email),
            onMouseLeave: () => setHoveredRow(null),
          })}
        />
      </Spin>
      {contextHolder}
      <NewUserModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        createUser={onCreate}
        existingEmails={existingEmails}
        users={usersAdmin}
      />
    </Layout>
  );
};

export default UsersContainer;
